import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AgentResponse, ChatMessage } from '@shared/models/agent.model';

@Injectable({
    providedIn: 'root'
})
export class AgentService {
    private apiUrl = `${environment.api.baseUrl}/agent/chat`;

    private _isOpen = new BehaviorSubject<boolean>(false);
    public isOpen$ = this._isOpen.asObservable();

    private _messages = new BehaviorSubject<ChatMessage[]>([]);
    public messages$ = this._messages.asObservable();

    private _isLoading = new BehaviorSubject<boolean>(false);
    public isLoading$ = this._isLoading.asObservable();

    // Context management
    private history: any[] = [];
    private summary: string | undefined;

    constructor(private http: HttpClient) { }

    toggleChat() {
        this._isOpen.next(!this._isOpen.value);
    }

    openChat() {
        this._isOpen.next(true);
    }

    closeChat() {
        this._isOpen.next(false);
    }

    sendMessage(message: string) {
        if (!message.trim()) return;

        // Add user message locally
        const userMsg: ChatMessage = {
            role: 'user',
            content: message,
            timestamp: new Date()
        };
        this.addMessage(userMsg);
        this._isLoading.next(true);

        // Prepare payload
        // Note: history should be ChatMessage[] but backend might expect specific format.
        // v3 spec says sending 'history' is required.
        const payload = {
            message: message,
            history: this.history,
            previousSummary: this.summary
            // context: ... (can be injected here later)
        };

        this.http.post<AgentResponse>(this.apiUrl, payload).pipe(
            tap(() => this._isLoading.next(false)),
        ).subscribe({
            next: (res) => {
                // Add assistant response
                const aiMsg: ChatMessage = {
                    role: 'assistant',
                    content: res.response,
                    widgets: res.widgets, // Store widgets to display
                    timestamp: new Date()
                };
                this.addMessage(aiMsg);

                // Update state from backend
                if (res.history) {
                    this.history = res.history;
                }
                if (res.summary) {
                    this.summary = res.summary;
                }
            },
            error: (err) => {
                console.error('Agent API Error', err);
                this._isLoading.next(false);
                this.addMessage({
                    role: 'system',
                    content: '시스템 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
                    timestamp: new Date()
                });
            }
        });
    }

    private addMessage(msg: ChatMessage) {
        const current = this._messages.value;
        this._messages.next([...current, msg]);
    }

    clearHistory() {
        this._messages.next([]);
        this.history = [];
        this.summary = undefined;
    }
}
